import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shared/PageHeader';
import { useSaveStatus } from '@/components/shared/SaveStatus';
import { apiErrorMessage } from '@/lib/apiErrorMessage';
import { formatCents } from '@/lib/formatCents';
import { useAdminMetric } from '@/hooks/useAdminMetric';
import { KpiCard } from '@/components/admin/metrics/KpiCard';
import { FunnelBar } from '@/components/admin/metrics/FunnelBar';
import { CohortMatrix } from '@/components/admin/metrics/CohortMatrix';
import { ChurnBlock } from '@/components/admin/metrics/ChurnBlock';
import { MarginCard } from '@/components/admin/metrics/MarginCard';
import { LlmUnitCard } from '@/components/admin/metrics/LlmUnitCard';
import { QuotaPressureList } from '@/components/admin/metrics/QuotaPressureList';
import { GatesPanel } from '@/components/admin/metrics/GatesPanel';
import { EventsFeed } from '@/components/admin/metrics/EventsFeed';
import {
  adminMetricsService,
  type MetricsSummary,
  type MetricsFunnel,
  type MetricsChurn,
  type MetricsCohorts,
  type MetricsMargin,
  type MetricsLlm,
  type MetricsQuotaPressure,
  type MetricsEvents,
  type MetricsGates,
  type MetricsWindow,
} from '@/services/adminMetricsService';

// ─── Section wrapper ─────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  children: React.ReactNode;
  'data-testid'?: string;
}

function Section({ title, children, 'data-testid': testId }: SectionProps) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// ─── Section skeletons ────────────────────────────────────────────────────────

function KpiSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} data-testid="kpi-skeleton" className="h-24 rounded-xl" />
      ))}
    </div>
  );
}

function GenericSkeleton({ testId }: { testId: string }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} data-testid={testId} className="h-8 w-full rounded-md" />
      ))}
    </div>
  );
}

// ─── Inline error with retry ──────────────────────────────────────────────────

interface InlineErrorProps {
  message: string;
  onRetry: () => void;
  testId: string;
}

function InlineError({ message, onRetry, testId }: InlineErrorProps) {
  const { t } = useTranslation();
  return (
    <div
      data-testid={testId}
      role="alert"
      className="flex items-center gap-3 text-sm text-destructive"
    >
      <span>{message}</span>
      <Button variant="ghost" size="sm" onClick={onRetry} aria-label={t('admin.metrics.retry')}>
        {t('admin.metrics.retry')}
      </Button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AdminMetricsPage() {
  const { t } = useTranslation();
  const [window_, setWindow] = useState<MetricsWindow>('month');

  // Each section fetches independently so failures are isolated.
  const [summaryState, reloadSummary] = useAdminMetric<MetricsSummary>(
    useCallback(() => adminMetricsService.getSummary(), [])
  );

  const [funnelState, reloadFunnel] = useAdminMetric<MetricsFunnel>(
    useCallback(() => adminMetricsService.getFunnel(window_), [window_])
  );

  const [churnState, reloadChurn] = useAdminMetric<MetricsChurn>(
    useCallback(() => adminMetricsService.getChurn(window_), [window_])
  );

  const [cohortsState, reloadCohorts] = useAdminMetric<MetricsCohorts>(
    useCallback(() => adminMetricsService.getCohorts(), [])
  );

  const [marginState, reloadMargin] = useAdminMetric<MetricsMargin>(
    useCallback(() => adminMetricsService.getMargin(window_), [window_])
  );

  const [llmState, reloadLlm] = useAdminMetric<MetricsLlm>(
    useCallback(() => adminMetricsService.getLlmUnitEconomics(window_), [window_])
  );

  const [quotaState, reloadQuota] = useAdminMetric<MetricsQuotaPressure>(
    useCallback(() => adminMetricsService.getQuotaPressure(), [])
  );

  const [eventsState, reloadEvents] = useAdminMetric<MetricsEvents>(
    useCallback(() => adminMetricsService.getEvents(), [])
  );

  const [gatesState, reloadGates] = useAdminMetric<MetricsGates>(
    useCallback(() => adminMetricsService.getGates(), [])
  );

  // F103/A25 — the CAC save used to swallow errors in a silent ``catch {}``.
  // Drive it through SaveStatus so the founder sees saving/saved/error + retry.
  const cacSave = useSaveStatus();
  function handleSaveCac(euros: number) {
    const cents = Math.round(euros * 100);
    void cacSave.save(async () => {
      await adminMetricsService.updateCac(cents);
      reloadGates();
    });
  }

  // ── KPI section ────────────────────────────────────────────────────────────

  function renderKpi() {
    if (summaryState.status === 'loading') return <KpiSkeleton />;
    if (summaryState.status === 'error') {
      return (
        <InlineError message={summaryState.error} onRetry={reloadSummary} testId="kpi-error" />
      );
    }
    const s = summaryState.data;
    const netChurn = churnState.status === 'ok' ? churnState.data.mrr_net_cents : null;
    const netBrl = netChurn?.BRL ?? 0;
    const netEur = netChurn?.EUR ?? 0;
    const netPositive = netBrl >= 0;

    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <KpiCard
          title={t('admin.metrics.kpi.mrr_brl')}
          value={formatCents(s.mrr_cents.BRL, 'BRL')}
          secondary={`${t('admin.metrics.kpi.arpa')}: ${formatCents(s.arpa_cents.BRL, 'BRL')}`}
        />
        <KpiCard
          title={t('admin.metrics.kpi.mrr_eur')}
          value={formatCents(s.mrr_cents.EUR, 'EUR')}
          secondary={`${t('admin.metrics.kpi.normalized')}: ${formatCents(s.normalized_mrr_cents, 'EUR')} aprox`}
        />
        <KpiCard
          title={t('admin.metrics.kpi.active')}
          value={s.counts.active}
          data-testid="kpi-active-count"
        />
        <KpiCard
          title={t('admin.metrics.kpi.trialing')}
          value={s.counts.trialing}
          data-testid="kpi-trialing-count"
        />
        <KpiCard
          title={t('admin.metrics.kpi.past_due')}
          value={s.counts.past_due}
          valueVariant={s.counts.past_due > 0 ? 'warning' : 'default'}
        />
        <KpiCard
          title={t('admin.metrics.kpi.net_mrr')}
          value={
            churnState.status === 'ok' ? (
              <>
                <span>
                  {netPositive ? '+' : ''}
                  {formatCents(netBrl, 'BRL')}
                </span>
                <div className="text-sm font-normal mt-0.5">
                  {netPositive ? '+' : ''}
                  {formatCents(netEur, 'EUR')}
                </div>
              </>
            ) : (
              '—'
            )
          }
          valueVariant={
            churnState.status === 'ok' ? (netPositive ? 'success' : 'destructive') : 'default'
          }
        />
      </div>
    );
  }

  // ── Funnel section ─────────────────────────────────────────────────────────

  function renderFunnel() {
    if (funnelState.status === 'loading') {
      return <GenericSkeleton testId="funnel-skeleton" />;
    }
    if (funnelState.status === 'error') {
      return (
        <InlineError message={funnelState.error} onRetry={reloadFunnel} testId="funnel-error" />
      );
    }
    const f = funnelState.data;
    const steps = [
      {
        labelKey: 'admin.metrics.funnel.signups',
        count: f.signups,
        max: f.signups,
        testId: 'funnel-signups',
      },
      {
        labelKey: 'admin.metrics.funnel.trials',
        count: f.trials,
        max: f.signups,
        pct: f.signup_to_trial_pct,
        pctLabelKey: 'admin.metrics.funnel.signup_to_trial',
        testId: 'funnel-signup-to-trial-pct',
      },
      {
        labelKey: 'admin.metrics.funnel.paid',
        count: f.paid,
        max: f.signups,
        pct: f.trial_to_paid_pct,
        pctLabelKey: 'admin.metrics.funnel.trial_to_paid',
      },
      {
        labelKey: 'admin.metrics.funnel.churned',
        count: f.churned,
        max: f.signups,
      },
    ];

    return (
      <div>
        {/* Render hidden spans to expose testable counts */}
        <span data-testid="funnel-signups" className="sr-only">
          {f.signups}
        </span>
        <span data-testid="funnel-signup-to-trial-pct" className="sr-only">
          {f.signup_to_trial_pct}
        </span>
        <FunnelBar steps={steps} />
      </div>
    );
  }

  // ── Churn section ──────────────────────────────────────────────────────────

  function renderChurn() {
    const loading = churnState.status === 'loading' || cohortsState.status === 'loading';
    if (loading) return <GenericSkeleton testId="churn-skeleton" />;
    if (churnState.status === 'error') {
      return (
        <InlineError
          message={churnState.error}
          onRetry={() => {
            reloadChurn();
            reloadCohorts();
          }}
          testId="churn-error"
        />
      );
    }

    const atRisk = summaryState.status === 'ok' ? summaryState.data.counts.past_due : 0;
    const cohorts = cohortsState.status === 'ok' ? cohortsState.data.cohorts : [];

    return (
      <div className="space-y-6">
        <ChurnBlock data={churnState.data} atRiskCount={atRisk} formatCents={formatCents} />
        <div className="mt-4">
          <h4 className="mb-3 text-sm font-medium text-muted-foreground">
            {t('admin.metrics.cohorts.title')}
          </h4>
          <CohortMatrix cohorts={cohorts} />
        </div>
      </div>
    );
  }

  // ── Margin section ─────────────────────────────────────────────────────────

  function renderMargin() {
    if (marginState.status === 'loading') return <GenericSkeleton testId="margin-skeleton" />;
    if (marginState.status === 'error') {
      return (
        <InlineError message={marginState.error} onRetry={reloadMargin} testId="margin-error" />
      );
    }
    return <MarginCard data={marginState.data} formatCents={formatCents} />;
  }

  // ── LLM unit economics section (F130) ──────────────────────────────────────

  function renderLlm() {
    if (llmState.status === 'loading') return <GenericSkeleton testId="llm-skeleton" />;
    if (llmState.status === 'error') {
      return <InlineError message={llmState.error} onRetry={reloadLlm} testId="llm-error" />;
    }
    return <LlmUnitCard data={llmState.data} />;
  }

  // ── Quota section ──────────────────────────────────────────────────────────

  function renderQuota() {
    if (quotaState.status === 'loading') return <GenericSkeleton testId="quota-skeleton" />;
    if (quotaState.status === 'error') {
      return <InlineError message={quotaState.error} onRetry={reloadQuota} testId="quota-error" />;
    }
    return <QuotaPressureList tenants={quotaState.data.tenants} />;
  }

  // ── Tier mix section ───────────────────────────────────────────────────────

  function renderTierMix() {
    if (summaryState.status === 'loading') return <GenericSkeleton testId="tier-skeleton" />;
    if (summaryState.status === 'error') return null;
    const { pro, business, custom } = summaryState.data.tier_mix;
    return (
      <dl className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <dt className="text-muted-foreground">{t('admin.metrics.tier.pro')}</dt>
          <dd className="mt-1 text-xl font-bold tabular-nums">{pro}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('admin.metrics.tier.business')}</dt>
          <dd className="mt-1 text-xl font-bold tabular-nums">{business}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('admin.metrics.tier.custom')}</dt>
          <dd className="mt-1 text-xl font-bold tabular-nums">{custom}</dd>
        </div>
      </dl>
    );
  }

  // ── Gates section ──────────────────────────────────────────────────────────

  function renderGates() {
    if (gatesState.status === 'loading') return <GenericSkeleton testId="gates-skeleton" />;
    if (gatesState.status === 'error') {
      return <InlineError message={gatesState.error} onRetry={reloadGates} testId="gates-error" />;
    }
    return (
      <GatesPanel
        data={gatesState.data}
        onSaveCac={handleSaveCac}
        cacSaveStatus={cacSave.status}
        cacSaveErrorMessage={cacSave.error ? apiErrorMessage(cacSave.error, t) : undefined}
        onRetryCac={() => void cacSave.retry()}
      />
    );
  }

  // ── Events section ─────────────────────────────────────────────────────────

  function renderEvents() {
    if (eventsState.status === 'loading') return <GenericSkeleton testId="events-skeleton" />;
    if (eventsState.status === 'error') {
      return (
        <InlineError message={eventsState.error} onRetry={reloadEvents} testId="events-error" />
      );
    }
    return <EventsFeed events={eventsState.data.events} formatCents={formatCents} />;
  }

  // ── Window toggle ──────────────────────────────────────────────────────────

  function WindowToggle() {
    return (
      <div className="flex items-center gap-1">
        <Button
          variant={window_ === 'month' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setWindow('month')}
          aria-label={t('admin.metrics.window_month')}
        >
          {t('admin.metrics.window_month')}
        </Button>
        <Button
          variant={window_ === '30d' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setWindow('30d')}
          aria-label={t('admin.metrics.window_30d')}
        >
          {t('admin.metrics.window_30d')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin.metrics.title')}
        subtitle={t('admin.metrics.subtitle')}
        actions={<WindowToggle />}
      />

      {/* 1. KPI row */}
      {renderKpi()}

      {/* 2. Funnel */}
      <Section title={t('admin.metrics.funnel.title')} data-testid="funnel-section">
        {renderFunnel()}
      </Section>

      {/* 3. Churn & retention */}
      <Section title={t('admin.metrics.churn.title')} data-testid="churn-section">
        {renderChurn()}
      </Section>

      {/* 4 & 5. Tier mix + Margin — side by side on md+ */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Section title={t('admin.metrics.tier.title')} data-testid="tier-mix-section">
          {renderTierMix()}
        </Section>

        <Section title={t('admin.metrics.margin.title')} data-testid="margin-section">
          {renderMargin()}
        </Section>

        <div className="md:col-span-2">
          <Section title={t('admin.metrics.llm.title')} data-testid="llm-section">
            {renderLlm()}
          </Section>
        </div>
      </div>

      {/* 6. Quota pressure */}
      <Section title={t('admin.metrics.quota.title')}>{renderQuota()}</Section>

      {/* 7. Gates §24 */}
      <Section title={t('admin.metrics.gates.title')}>{renderGates()}</Section>

      {/* 8. Events feed */}
      <Section title={t('admin.metrics.events.title')} data-testid="events-feed">
        {renderEvents()}
      </Section>
    </div>
  );
}
